/**
 * The Google and Apple options below the one-time-code form.
 *
 * Two rules live here:
 *
 *  - A provider that cannot possibly work draws nothing. Not a disabled button,
 *    not an explanation. When neither provider is usable the whole panel is
 *    absent, so the divider never sits over empty space.
 *  - A cancellation is silent and a real failure is the shared error modal.
 *    Closing a provider popup leaves the screen exactly as it was.
 */
import {
  useOauthAppleVerify,
  useOauthGoogleVerify,
  useOauthProviderConfigGet,
} from "@sportsmet/auth-sdk/public";
import { type ReactNode } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { AppleSignInButton } from "./AppleSignInButton";
import { GoogleSignInButton } from "./GoogleSignInButton";

/**
 * A usable id is a non-empty string. Null, undefined, and "" all mean the
 * operator has not configured this provider, so there is nothing to hand the
 * provider's JavaScript and nothing to draw.
 */
function isUsableId(candidate: string | null | undefined): candidate is string {
  return typeof candidate === "string" && candidate !== "";
}

export type typeProviderSignInPanelProps = {
  /** Called with a real session. The screen writes the cookies and navigates. */
  onSignedIn: (args: { accessToken: string; refreshToken: string }) => void;
};

export const ProviderSignInPanel = ({
  onSignedIn,
}: typeProviderSignInPanelProps): ReactNode => {
  const providerConfig = useOauthProviderConfigGet({});
  const google = providerConfig.data?.data?.google;
  const apple = providerConfig.data?.data?.apple;

  // Both signals are required. `isEnabled` is the operator's switch for the
  // provider, and the web id is what the browser actually hands to the
  // provider's JavaScript. A platform can have the switch on with no web id
  // configured (only an iOS bundle id, say), and that browser has nothing to
  // sign in with.
  const googleWebClientId =
    google?.isEnabled === true && isUsableId(google.webClientId)
      ? google.webClientId
      : null;
  const appleWebServicesId =
    apple?.isEnabled === true && isUsableId(apple.webServicesId)
      ? apple.webServicesId
      : null;

  const verifyGoogle = useOauthGoogleVerify();
  const verifyApple = useOauthAppleVerify();

  // Nothing to offer, and nothing rendered. This also covers the moment before
  // the config query resolves, when both values are still undefined.
  if (googleWebClientId === null && appleWebServicesId === null) return null;

  const completeSignIn = (result: unknown): void => {
    if (showApiError(result, "providerSignIn")) return;
    const session = (
      result as {
        data?: { accessToken?: string; refreshToken?: string } | null;
      }
    ).data;
    const accessToken = session?.accessToken;
    const refreshToken = session?.refreshToken;
    if (accessToken === undefined || refreshToken === undefined) return;
    onSignedIn({ accessToken, refreshToken });
  };

  // `@sportsmet/ui` has no divider component, so this is three spans rather
  // than a kit import. Do not go looking for one.
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-line" />
        <span className="text-caption text-ink-tertiary">or</span>
        <span className="h-px flex-1 bg-line" />
      </div>
      {googleWebClientId !== null ? (
        <GoogleSignInButton
          webClientId={googleWebClientId}
          onIdToken={({ idToken }) => {
            verifyGoogle.mutate({ idToken }, { onSuccess: completeSignIn });
          }}
        />
      ) : null}
      {appleWebServicesId !== null ? (
        <AppleSignInButton
          webServicesId={appleWebServicesId}
          onIdToken={({ idToken }) => {
            verifyApple.mutate({ idToken }, { onSuccess: completeSignIn });
          }}
          onFailed={() => {
            // The one client-raised code in this flow. `showApiError` reads
            // only `error.code` and maps it through the shared catalog, so the
            // message is deliberately empty.
            showApiError({
              error: { code: "PROVIDER_UNAVAILABLE", message: "" },
              data: null,
            });
          }}
        />
      ) : null}
    </div>
  );
};
